import { useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import type { DownstreamPreview } from '@/features/corrections/hooks';

interface DownstreamConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  preview: DownstreamPreview | null;
  confirming?: boolean;
  onConfirm: () => void;
}

/**
 * Confirmation for an edit that reaches past the field the operator
 * changed — reversing a receipt, pulling stock back out, or rewriting a
 * cost that other screens read.
 */
export function DownstreamConfirmDialog({
  open,
  onOpenChange,
  preview,
  confirming,
  onConfirm,
}: DownstreamConfirmDialogProps) {
  const groups = useMemo(() => {
    const map = new Map<string, string[]>();
    for (const effect of preview?.effects ?? []) {
      const list = map.get(effect.area) ?? [];
      list.push(effect.detail);
      map.set(effect.area, list);
    }
    return Array.from(map.entries());
  }, [preview]);

  const major = preview?.severity === 'major';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="z-[80] max-h-[85vh] max-w-lg overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{preview?.title || 'Confirm changes'}</DialogTitle>
          {preview?.summary && (
            <DialogDescription>{preview.summary}</DialogDescription>
          )}
        </DialogHeader>

        <div className="space-y-4">
          {groups.map(([area, details]) => (
            <section key={area} className="space-y-1.5">
              <h3 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                {area}
              </h3>
              <ul className="space-y-1.5 text-sm">
                {details.map((detail, index) => (
                  <li
                    key={`${area}-${index}`}
                    className="rounded-md border border-border bg-secondary/40 px-3 py-2"
                  >
                    {detail}
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={confirming}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant={major ? 'destructive' : 'default'}
            onClick={onConfirm}
            disabled={confirming}
          >
            {confirming ? 'Applying…' : major ? 'Apply these changes' : 'Update related records'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
